import { ReactiveController, ReactiveControllerHost } from "lit";
import { Plugin, EditorState, Transaction } from "prosemirror-state";
import { EditorView } from "prosemirror-view";
import { Node } from "prosemirror-model";

import { EditorConfig } from "@/wc/text-editor/lib/core/config";

import {
    EditorHostInterface,
    HOST_EVENT_PLUGIN,
} from "@/wc/text-editor/lib/core/plugins/host-events.js";
import { MenuItemState } from "../core/menu";
import { serialize } from "@/wc/text-editor/lib/core/config/serializer.js";

const updateHook = (
    updateViewCallback: () => void,
    dispatchEvent: (name: string, detail?: unknown) => void,
    getController: () => ProseMirrorViewController
) =>
    new Plugin<EditorHostInterface>({
        key: HOST_EVENT_PLUGIN,
        view: () => {
            return {
                update: updateViewCallback,
            };
        },
        state: {
            init() {
                return {
                    dispatchEvent,
                    getController,
                };
            },
            apply(tr: Transaction, value) {
                if (tr.docChanged) {
                    value.dispatchEvent("changed");
                }
                return value;
            },
        },
    });

function createViewNode() {
    const viewNode = document.createElement("div");
    viewNode.setAttribute("part", "prosemirror-target");
    return viewNode;
}

export class ProseMirrorViewController implements ReactiveController {
    host: ReactiveControllerHost & HTMLElement;

    viewNode: Element = createViewNode();
    _view?: EditorView;
    menu: MenuItemState[] = [];
    _config: EditorConfig;
    readingMode: boolean;
    loadToken?: string;

    constructor(
        host: ReactiveControllerHost & HTMLElement,
        config: EditorConfig,
        readingMode: boolean
    ) {
        this._config = config;
        this.host = host;
        this.readingMode = readingMode;

        // Set controller last, since it will act on set properties.
        this.host.addController(this);
    }

    hostConnected() {
        this._view = new EditorView(this.viewNode, {
            state: this.createState(),
            ...this.modeProps(),
        });
        this.updateMenu();
    }

    updateMenu() {
        if (this._view) {
            this.menu = this._config.menu(this._view);
        }
        this.host.requestUpdate();
    }

    serialize() {
        return this._view
            ? serialize(this._config, this._view?.state?.doc)
            : "";
    }

    createState(doc?: Node) {
        const config = {
            schema: this._config.schema(),
            plugins: [
                ...this._config.plugins(this._config.schema()),
                updateHook(
                    () => {},
                    (name, detail) => {
                        this.viewNode.dispatchEvent(
                            new CustomEvent(
                                `${this.host.tagName.toLowerCase()}:${name}`,
                                {
                                    bubbles: true,
                                    composed: true,
                                    detail: detail,
                                }
                            )
                        );
                    },
                    () => this
                ),
            ],
            doc,
        };
        return EditorState.create(config);
    }

    load(source: string) {
        this.loadToken = source;
        this._config.parse(source).then((node) => {
            if (source === this.loadToken) {
                // Don't load the document when this call to "load" was superseded
                // by another call.
                if (this._view) {
                    const transaction = this._view.state.tr.replaceWith(
                        0,
                        this._view.state.doc.content.size,
                        node.content
                    );
                    this._view.dispatch(transaction);
                }
            }
        });
    }

    setReadingMode(readingMode: boolean) {
        this.readingMode = readingMode;
        this._view?.setProps(this.modeProps());
    }

    modeProps() {
        return {
            editable: () => !this.readingMode,
            attributes: () => ({
                class: this.readingMode
                    ? "document reading-mode"
                    : "document edit-mode",
                part: `editor-document${
                    this.readingMode
                        ? " editor-document-reading-mode"
                        : " editor-document-edit-mode"
                }`,
            }),
        };
    }
}
